import { ArrowLeftOutlined } from "@ant-design/icons";
import { Avatar, Breadcrumb, Button, Card, Flex, Skeleton, Tabs, Typography } from "antd";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import ComplianceClientsTable from "../../components/Compliance/ComplianceClientsTable.component";
import ComplianceNotesTable from "../../components/Compliance/ComplianceNotesTable.component";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  useGetComplianceClinicianClientsQuery,
  useGetComplianceClinicianDetailQuery,
} from "../../service/compliance.service";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import styles from "./compliance.module.scss";

const PAGE_SIZE = 20;
const CLIENTS_PAGE_SIZE = 20;

const ClinicianComplianceDetail = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { userInfo } = useAppSelector((state) => state.auth);

  const [notesPage, setNotesPage] = useState(1);
  const [clientUuid, setClientUuid] = useState<string | undefined>(undefined);
  const [clientsPage, setClientsPage] = useState(1);

  const numericUserId = Number(userId);
  const queryEnabled = !Number.isNaN(numericUserId);

  const notesTabLabel = userInfo?.practice_area === PRACTICE_AREA.MEDICINE ? "Encounters" : "Sessions";

  const { data, isLoading, isFetching } = useGetComplianceClinicianDetailQuery(
    {
      userId: numericUserId,
      page: notesPage,
      pageSize: PAGE_SIZE,
      clientUuid,
    },
    { skip: !queryEnabled },
  );
  const { data: clientsData, isFetching: clientsFetching } = useGetComplianceClinicianClientsQuery(
    { userId: numericUserId, page: clientsPage, pageSize: CLIENTS_PAGE_SIZE },
    { skip: !queryEnabled },
  );

  if (!userInfo?.is_compliance_enabled || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }
  if (!queryEnabled) {
    return <Navigate to="/compliance/clinicians" replace />;
  }

  if (isLoading || !data) {
    return (
      <div className={styles.compliance__container}>
        <Skeleton active avatar={{ size: 56 }} title={{ width: "40%" }} paragraph={{ rows: 1 }} />
        <Skeleton active title={false} paragraph={{ rows: 8 }} style={{ marginTop: 32 }} />
      </div>
    );
  }

  const { clinician, notes, clients_for_filter, has_active_rules } = data;

  return (
    <div className={styles.compliance__container}>
      <Flex align="center" gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/compliance/clinicians")}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance
                </a>
              ),
            },
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance/clinicians");
                  }}
                >
                  Clinicians
                </a>
              ),
            },
            { title: clinician.display_name },
          ]}
        />
      </Flex>

      <Card style={{ marginBottom: 16 }} className={styles.compliance__clinicianHeader}>
        <Flex gap={16} align="center" wrap="wrap">
          <Avatar size={56} style={{ backgroundColor: getRandomAvatarColor(clinician.display_name) }}>
            {clinician.avatar_initials}
          </Avatar>
          <Flex vertical style={{ minWidth: 0, flex: 1 }}>
            <Typography.Title level={3} style={{ margin: 0, wordBreak: "break-word" }}>
              {clinician.display_name}
            </Typography.Title>
            <Typography.Text style={{ color: "#6b7280" }}>{clinician.specialty || "Specialty not set"}</Typography.Text>
            <Typography.Text style={{ color: "#6b7280", wordBreak: "break-all" }}>{clinician.email}</Typography.Text>
          </Flex>
        </Flex>
      </Card>

      <Tabs
        defaultActiveKey="notes"
        items={[
          {
            key: "notes",
            label: notesTabLabel,
            children: (
              <ComplianceNotesTable
                clinicianUserId={numericUserId}
                notes={notes.results}
                total={notes.count}
                currentPage={notes.page}
                pageSize={notes.page_size}
                onPageChange={setNotesPage}
                loading={isFetching}
                selectedClientUuid={clientUuid}
                onClientFilterChange={(uuid) => {
                  setClientUuid(uuid);
                  setNotesPage(1);
                }}
                clients={clients_for_filter}
                hasActiveRules={has_active_rules}
              />
            ),
          },
          {
            key: "clients",
            label: "Clients",
            children: (
              <ComplianceClientsTable
                clients={clientsData?.results ?? []}
                total={clientsData?.count ?? 0}
                currentPage={clientsData?.page ?? 1}
                pageSize={clientsData?.page_size ?? 20}
                onPageChange={setClientsPage}
                loading={clientsFetching}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default ClinicianComplianceDetail;
